import {
  useMemo,
  useState,
  type CSSProperties,
  type ComponentType,
  type ReactNode,
} from "react";
import {
  BellRing,
  Blocks,
  ChartNoAxesCombined,
  ClipboardCheck,
  Clock3,
  Database,
  LogOut,
  Menu,
  ScrollText,
  Users,
  X,
} from "lucide-react";
import type { AdminIdentity, RouteKey } from "../types";
import { BrandMark } from "./design-system";

const navigation: Array<{
  key: RouteKey;
  label: string;
  icon: ComponentType<{ size?: number; strokeWidth?: number }>;
}> = [
  { key: "overview", label: "总览", icon: ChartNoAxesCombined },
  { key: "users", label: "用户", icon: Users },
  { key: "publications", label: "发布", icon: BellRing },
  { key: "auto-dorm-check", label: "自动查寝", icon: ClipboardCheck },
  { key: "data", label: "数据", icon: Database },
  { key: "jobs", label: "任务", icon: Clock3 },
  { key: "audit", label: "审计", icon: ScrollText },
];

export function Shell({
  admin,
  route,
  onRoute,
  onLogout,
  loggingOut,
  children,
}: {
  admin: AdminIdentity;
  route: RouteKey;
  onRoute: (route: RouteKey) => void;
  onLogout: () => void;
  loggingOut: boolean;
  children: ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const current =
    navigation.find((item) => item.key === route) || navigation[0];
  const activeIndex = Math.max(
    0,
    navigation.findIndex((item) => item.key === route),
  );
  const timeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "本地时区",
    [],
  );

  const navigate = (next: RouteKey) => {
    setMobileOpen(false);
    onRoute(next);
  };

  return (
    <div className="admin-shell-v2">
      <button
        className={`sidebar-backdrop-v2 ${mobileOpen ? "is-visible" : ""}`}
        aria-label="关闭菜单"
        onClick={() => setMobileOpen(false)}
      />
      <aside className={`sidebar-v2 ${mobileOpen ? "is-open" : ""}`}>
        <div className="flex h-[76px] items-center gap-3 border-b border-sand-light/70 px-6">
          <BrandMark size={36} />
          <div>
            <strong className="font-display text-[16px] font-medium tracking-wide text-ink">
              Easy SWU
            </strong>
            <p className="eyebrow mt-0.5 text-[8px] text-ink-faint">ADMIN</p>
          </div>
          <button
            className="ml-auto rounded-lg p-2 text-ink-soft hover:bg-peach lg:hidden"
            aria-label="关闭菜单"
            onClick={() => setMobileOpen(false)}
          >
            <X size={18} />
          </button>
        </div>

        <nav
          className="sidebar-navigation-v2"
          aria-label="管理导航"
          style={{ "--active-index": activeIndex } as CSSProperties}
        >
          <span className="nav-selection-v2" aria-hidden="true" />
          {navigation.map((item) => {
            const ItemIcon = item.icon;
            return (
              <button
                key={item.key}
                className={`nav-item-v2 ${route === item.key ? "is-active" : ""}`}
                onClick={() => navigate(item.key)}
                aria-current={route === item.key ? "page" : undefined}
              >
                <ItemIcon size={17} strokeWidth={1.8} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="mt-auto border-t border-sand-light/70 p-4">
          <div className="flex items-center gap-3 rounded-xl px-2 py-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink font-display text-[13px] text-sand">
              {admin.username.slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <strong className="block truncate text-[12.5px] font-medium text-ink">
                {admin.username}
              </strong>
              <span className="text-[10.5px] text-ink-faint">
                Administrator
              </span>
            </div>
            <button
              className="rounded-lg p-2 text-ink-faint transition hover:bg-peach hover:text-clay-deep active:scale-90"
              onClick={onLogout}
              disabled={loggingOut}
              aria-label={loggingOut ? "正在退出" : "退出登录"}
              title={loggingOut ? "正在退出" : "退出登录"}
            >
              <LogOut size={16} className={loggingOut ? "animate-pulse" : ""} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main-area-v2">
        <header className="topbar-v2">
          <button
            className="rounded-lg p-2 text-ink-soft hover:bg-peach lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="打开菜单"
          >
            <Menu size={20} />
          </button>
          <div className="flex min-w-0 items-center gap-2">
            <Blocks size={15} className="hidden text-clay sm:block" />
            <h1 className="truncate font-display text-[15px] font-medium text-ink">
              {current.label}
            </h1>
          </div>
          <span className="ml-auto hidden text-[11px] text-ink-faint sm:block">
            浏览器时区 · {timeZone}
          </span>
        </header>
        <div className="content-area-v2" key={route}>
          {children}
        </div>
      </main>
    </div>
  );
}
